
import Vue from 'vue'
import { getCallbotMaterialAudio, getCallbotMaterials } from '@/api/callbot'
import { CallbotMaterial, CallbotMaterialFilter } from '@/types/callbot'
import MaterialDetailDialog from './MaterialDetailDialog.vue'
import UploadMaterialDialog from './UploadMaterialDialog.vue'
import ZaIcon from './ZaIcon.vue'
import {
    MATERIAL_FILTERS,
    MATERIAL_FORMAT_HINT,
    MATERIAL_STATUS_LABELS,
    formatMaterialDate,
    formatMaterialDuration,
    materialFilterCounts,
} from './audioLibrary'

// Пока ролики на модерации, список сам подтягивает новые статусы.
const POLL_INTERVAL_MS = 30_000

export default Vue.extend({
    name: 'AudioLibrary',
    components: { MaterialDetailDialog, UploadMaterialDialog, ZaIcon },
    props: {
        projectId: { type: String, required: true },
        canManage: { type: Boolean, default: false },
    },
    data() {
        return {
            items: [] as CallbotMaterial[],
            filter: 'all' as CallbotMaterialFilter,
            filters: MATERIAL_FILTERS,
            formatHint: MATERIAL_FORMAT_HINT,
            loading: false,
            error: '',
            uploadOpen: false,
            replaceTarget: null as CallbotMaterial | null,
            detailOpen: false,
            detailId: '',
            playingId: '',
            player: null as HTMLAudioElement | null,
            audioUrls: {} as Record<string, string>,
            pollTimer: 0,
        }
    },
    computed: {
        rows(): CallbotMaterial[] {
            return this.filter === 'all' ? this.items : this.items.filter(item => item.status === this.filter)
        },
        counts(): Record<CallbotMaterialFilter, number> {
            return materialFilterCounts(this.items)
        },
        detailTarget(): CallbotMaterial | null {
            return this.items.find(item => item.id === this.detailId) || null
        },
    },
    watch: {
        projectId() {
            this.stopPreview()
            this.load()
        },
    },
    created() {
        this.load()
        this.pollTimer = window.setInterval(() => {
            if (this.items.some(item => item.status === 'pending')) this.load(true)
        }, POLL_INTERVAL_MS)
    },
    beforeDestroy() {
        window.clearInterval(this.pollTimer)
        this.stopPreview()
        Object.values(this.audioUrls).forEach(url => URL.revokeObjectURL(url))
    },
    methods: {
        async load(silent = false) {
            if (!silent) this.loading = true
            const { data, error } = await getCallbotMaterials(this.projectId)
            this.loading = false
            if (error) {
                if (!silent) this.error = 'Не удалось загрузить аудиоролики'
                return
            }
            this.error = ''
            this.items = data || []
            this.$emit('loaded', this.items)
        },
        filterLabel(filter: CallbotMaterialFilter): string {
            return filter === 'all' ? 'Все' : MATERIAL_STATUS_LABELS[filter]
        },
        statusLabel(item: CallbotMaterial): string {
            return MATERIAL_STATUS_LABELS[item.status]
        },
        statusIcon(item: CallbotMaterial): string {
            return item.status === 'approved' ? 'check' : item.status === 'pending' ? 'clock' : 'error'
        },
        meta(item: CallbotMaterial): string {
            return `${formatMaterialDuration(item.durationSeconds)} · ${item.fileName}`
        },
        date(item: CallbotMaterial): string {
            return formatMaterialDate(item.createdAt)
        },
        openUpload(replace: CallbotMaterial | null = null) {
            this.stopPreview()
            this.replaceTarget = replace
            this.uploadOpen = true
        },
        openDetail(item: CallbotMaterial) {
            this.stopPreview()
            this.detailId = item.id
            this.detailOpen = true
        },
        onReplaceFromDetail(item: CallbotMaterial) {
            this.detailOpen = false
            this.openUpload(item)
        },
        onUse(item: CallbotMaterial) {
            this.detailOpen = false
            this.$emit('create-run', item)
        },
        async onSaved(material: CallbotMaterial) {
            this.$store.dispatch('callNotify', 'Аудио отправлено на модерацию')
            // Замена меняет файл — старая запись в кэше плеера больше не верна.
            if (this.audioUrls[material.id]) {
                URL.revokeObjectURL(this.audioUrls[material.id])
                this.$delete(this.audioUrls, material.id)
            }
            await this.load(true)
            this.filter = 'all'
            this.openDetail(material)
        },
        async togglePreview(item: CallbotMaterial) {
            if (this.playingId === item.id) {
                this.stopPreview()
                return
            }
            this.stopPreview()
            this.playingId = item.id
            let url = this.audioUrls[item.id]
            if (!url) {
                const blob = await getCallbotMaterialAudio(this.projectId, item.id)
                if (this.playingId !== item.id) return
                if (!blob) {
                    this.playingId = ''
                    this.$store.dispatch('callNotify', 'Запись недоступна')
                    return
                }
                url = URL.createObjectURL(blob)
                this.$set(this.audioUrls, item.id, url)
            }
            const player = new Audio(url)
            player.onended = () => this.stopPreview()
            this.player = player
            player.play().catch(() => this.stopPreview())
        },
        stopPreview() {
            if (this.player) {
                this.player.pause()
                this.player.onended = null
            }
            this.player = null
            this.playingId = ''
        },
    },
})
